/**
 * Kalender, Aufgaben, Dateien, Dokumente, Kommunikation, Wissen und
 * Administration.
 *
 * **Warum diese sieben zusammen in einer Datei liegen und in `src/` statt in
 * `features/`.** Für die anderen fünf Bereiche gibt es ein zuständiges Paket
 * mit eigenem `owner_path` — `PORTAL-001` das Dashboard, `PORTAL-002` Auftrag
 * und Kunde, `INBOX-001` den Eingang, `PORTAL-003` die Suche. Für diese sieben
 * gibt es keins: Sie sind die Sicht auf ein Fremdsystem, und was sie füllt,
 * kommt aus `NC-002` (Kalender, Dateien, Wissen), `VIK-002` (Aufgaben),
 * `PAP-002` (Dokumente) und `MAIL-002` (Kommunikation) — aus Adaptern also,
 * nicht aus Portalfunktionen.
 *
 * Einen Feature-Ordner für sie anzulegen hieße, einen Zuschnitt zu erfinden,
 * den das Backlog nicht kennt. Solange sie Listen auf verknüpfte Ressourcen
 * sind, gehören sie zur Shell (`UI-001`, `apps/portal/**`). Wächst einer davon
 * zu etwas Eigenem, bekommt er ein Paket — und dann auch einen eigenen Ordner.
 *
 * Allen sieben gemeinsam: Sie zeigen **Verweise, keine Inhalte**. Nach
 * `07` §7.5 kopiert die Plattform keine fremden Datenbestände, und nach
 * `01` §1.11 entscheidet über den Zugriff das Quellsystem beim Öffnen.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router";

import { hatQuellsystem,
  baldAblaufendeLinks,
  faelligeWartung,
  findeAuftrag,
  findeKunde,
  findePerson,
} from "@hwc/demo-data";
import type { Ablageart } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { Hinweis, Knopf } from "@hwc/ui";

import { useSitzung } from "../daten/Sitzung.js";
import { widerrufeLink } from "../daten/vorgaenge.js";
import {
  Einsatzabzeichen,
  Feld,
  Felder,
  Leer,
  Liste,
  Prioritaetsabzeichen,
  Zeile,
  datum,
  mehrzahl,
  groesse,
  uhrzeit,
  wochentag,
  zeitfenster,
  zumAuftrag,
  zumKunden,
} from "./bausteine.js";

// ───────────────────────────── Kalender ─────────────────────────────

/**
 * Kalender — führendes System ist Nextcloud (`01` §1.3).
 *
 * Einsätze und Termine stehen **in einer Liste**, nach Tagen gruppiert. Zwei
 * getrennte Listen wären technisch ehrlicher — ein Einsatz ist eine
 * Vikunja-Aufgabe, ein Termin ein Kalendereintrag — und praktisch unbrauchbar:
 * Wer wissen will, ob der Donnerstag voll ist, will nicht zwei Listen
 * gegeneinander halten.
 */
export function Kalender() {
  const { bestand, heute } = useSitzung();

  const tage = useMemo(() => {
    type Eintrag = {
      schluessel: string;
      zeit: string;
      titel: string;
      unterzeile: string;
      abzeichen?: React.ReactNode;
      ziel?: string;
    };
    const nach = new Map<string, Eintrag[]>();

    const einfuegen = (tag: string, eintrag: Eintrag) => {
      const liste = nach.get(tag) ?? [];
      liste.push(eintrag);
      nach.set(tag, liste);
    };

    for (const e of bestand.einsaetze) {
      if (e.zustand === "storniert") continue;
      const auftrag = findeAuftrag(bestand, e.auftrag);
      const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;
      einfuegen(e.von.slice(0, 10), {
        schluessel: `e-${e.ref.opaqueId}`,
        zeit: e.von,
        titel: e.titel,
        unterzeile: [
          kunde?.name,
          auftrag ? `Auftrag ${auftrag.nummer}` : undefined,
          findePerson(bestand, e.monteur)?.name ?? "nicht zugewiesen",
        ]
          .filter(Boolean)
          .join(" · "),
        abzeichen: (
          <>
            <Prioritaetsabzeichen wert={e.prioritaet} />
            <Einsatzabzeichen zustand={e.zustand} />
          </>
        ),
        ...(auftrag ? { ziel: zumAuftrag(auftrag.nummer) } : {}),
      });
    }

    for (const t of bestand.termine) {
      const auftrag = findeAuftrag(bestand, t.auftrag);
      einfuegen(t.von.slice(0, 10), {
        schluessel: `t-${t.ref.opaqueId}`,
        zeit: t.von,
        titel: t.titel,
        unterzeile: [
          t.ort,
          t.teilnehmer
            .map((r) => findePerson(bestand, r)?.kuerzel)
            .filter(Boolean)
            .join(", "),
        ]
          .filter(Boolean)
          .join(" · "),
        abzeichen: <span className="abzeichen abzeichen--neutral">Termin</span>,
        ...(auftrag ? { ziel: zumAuftrag(auftrag.nummer) } : {}),
      });
    }

    const heuteTag = heute.toISOString().slice(0, 10);
    return [...nach.entries()]
      // Vergangenes fällt weg: Ein Kalender, der beim Öffnen in der
      // Vergangenheit steht, wird jedes Mal erst gescrollt.
      .filter(([tag]) => tag >= heuteTag)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([tag, liste]) => [tag, liste.sort((x, y) => x.zeit.localeCompare(y.zeit))] as const);
  }, [bestand, heute]);

  return (
    <>
      <div className="bereichskopf">
        <h1>Kalender</h1>
        <span className="bereichskopf__zusatz">ab heute</span>
      </div>

      {tage.length === 0 ? (
        <Leer text="Ab heute ist nichts eingetragen." />
      ) : (
        tage.map(([tag, eintraege]) => (
          <div key={tag}>
            <h2>
              {wochentag(tag)} <span className="leise">{datum(tag)}</span>
            </h2>
            <Liste>
              {eintraege.map((e) => (
                <Zeile
                  key={e.schluessel}
                  {...(e.ziel ? { zu: e.ziel } : {})}
                  titel={e.titel}
                  unterzeile={e.unterzeile}
                  abzeichen={e.abzeichen}
                  rechts={uhrzeit(e.zeit)}
                />
              ))}
            </Liste>
          </div>
        ))
      )}

      <p className="notiz">
        Führendes System ist <strong>Nextcloud</strong> (`01` §1.3). Einsätze stammen aus
        Vikunja und erscheinen hier in derselben Liste, weil ein Tag nicht nach
        Quellsystemen zerfällt.
      </p>
    </>
  );
}

// ───────────────────────────── Aufgaben ─────────────────────────────

/**
 * Aufgaben — führendes System ist Vikunja (`07` §7.6).
 *
 * Ein Einsatz ist eine Vikunja-Aufgabe, ein Arbeitsschritt eine Unteraufgabe
 * (ADR-015). Die Ansicht zeigt deshalb genau das und erfindet keine dritte
 * Aufgabenart: `01` §1.11 erlaubt kein zweites Aufgabensystem, und eine
 * „Portal-Aufgabe" wäre der Anfang davon.
 */
export function Aufgaben() {
  const { bestand, heute, person } = useSitzung();
  const [nurMeine, setzeNurMeine] = useState(false);

  const offene = useMemo(
    () =>
      bestand.einsaetze
        .filter((e) => e.zustand !== "geprueft" && e.zustand !== "storniert")
        .filter(
          (e) =>
            !nurMeine
            || (e.monteur !== undefined
              && formatResourceRef(e.monteur) === formatResourceRef(person.ref)),
        )
        .slice()
        .sort((x, y) => x.von.localeCompare(y.von)),
    [bestand, nurMeine, person],
  );

  const wartung = faelligeWartung(bestand, heute);
  const heuteTag = heute.toISOString().slice(0, 10);

  return (
    <>
      <div className="bereichskopf">
        <h1>Aufgaben</h1>
        <span className="bereichskopf__zusatz">{offene.length} offen</span>
      </div>

      <div className="filter">
        <button
          type="button"
          className={`filter__knopf${nurMeine ? "" : " filter__knopf--aktiv"}`}
          onClick={() => setzeNurMeine(false)}
          aria-pressed={!nurMeine}
        >
          Alle
        </button>
        <button
          type="button"
          className={`filter__knopf${nurMeine ? " filter__knopf--aktiv" : ""}`}
          onClick={() => setzeNurMeine(true)}
          aria-pressed={nurMeine}
        >
          Mir zugewiesen
        </button>
      </div>

      {offene.length === 0 ? (
        <Leer
          text={
            nurMeine
              ? `${person.name} hat keine offenen Einsätze.`
              : "Keine offenen Einsätze."
          }
        />
      ) : (
        <Liste>
          {offene.map((e) => {
            const auftrag = findeAuftrag(bestand, e.auftrag);
            const offen = e.schritte.filter((s) => !s.erledigt).length;
            const ueberfaellig = e.von.slice(0, 10) < heuteTag;
            return (
              <Zeile
                key={e.ref.opaqueId}
                zu={auftrag ? zumAuftrag(auftrag.nummer) : undefined}
                titel={e.titel}
                unterzeile={
                  <>
                    {auftrag ? `Auftrag ${auftrag.nummer} · ` : ""}
                    {findePerson(bestand, e.monteur)?.name ?? "nicht zugewiesen"} ·{" "}
                    {offen} von {e.schritte.length} Schritten offen
                  </>
                }
                abzeichen={
                  <>
                    {ueberfaellig && (
                      <span className="abzeichen abzeichen--fehler">überfällig</span>
                    )}
                    <Einsatzabzeichen zustand={e.zustand} />
                  </>
                }
                rechts={`${datum(e.von)}\n${zeitfenster(e.von, e.bis)}`}
              />
            );
          })}
        </Liste>
      )}

      {wartung.length > 0 && (
        <>
          <h2>Wartung im Vorlauf</h2>
          <Liste>
            {wartung.map((p) => (
              <Zeile
                key={p.ref.opaqueId}
                zu={
                  findeKunde(bestand, p.kunde)
                    ? zumKunden(findeKunde(bestand, p.kunde)!.nummer)
                    : undefined
                }
                titel={p.titel}
                unterzeile={`${findeKunde(bestand, p.kunde)?.name ?? ""} · alle ${p.intervallMonate} Monate`}
                rechts={`fällig ${datum(p.naechsteFaelligkeit)}`}
              />
            ))}
          </Liste>
          <p className="notiz">
            Aus Wartungsplänen erzeugte Einsätze baut <code>MAINT-001</code> (Phase 7).
            Bis dahin zeigt diese Liste, was fällig wird — angelegt wird von Hand.{" "}
            <Link to="/wartung">Alle Wartungspläne</Link> — dort lässt sich ein Plan
            pausieren, verschieben, überspringen oder nachholen.
          </p>
        </>
      )}

      <p className="notiz">
        Führendes System ist <strong>Vikunja</strong>. Ein Einsatz ist eine Aufgabe, ein
        Arbeitsschritt eine Unteraufgabe mit eigenem ResourceRef (ADR-015) — keine
        Ankreuzkästchen im Beschreibungstext. Nextcloud Deck bleibt deaktiviert
        (`01` §1.11).
      </p>
    </>
  );
}

// ─────────────────────── Dateien, Dokumente, Wissen ───────────────────────

const ABLAGE: Record<
  Ablageart,
  { titel: string; quelle: string; erklaerung: string }
> = {
  arbeitsdatei: {
    titel: "Dateien",
    quelle: "Nextcloud",
    erklaerung:
      "Arbeitsdateien: was gerade bearbeitet wird. Wer etwas rechtssicher archivieren "
      + "will, legt es nach Paperless — eine automatische Doppelablage gibt es nach "
      + "`07` §7.12 bewusst nicht.",
  },
  dokument: {
    titel: "Dokumente",
    quelle: "Paperless",
    erklaerung:
      "Archivierte Dokumente: Eingangsrechnungen, Protokolle, Verträge. Das Original "
      + "bleibt unverändert; die E-Rechnung behält zusätzlich Hash und "
      + "Validierungsprotokoll (`07` §7.16).",
  },
  wissen: {
    titel: "Wissen",
    quelle: "Nextcloud Collectives",
    erklaerung:
      "Betriebliches Wissen als Markdown-Seiten: Fehlercodes, Anleitungen, "
      + "Checklisten. In der Monteur-App erscheint es kontextbezogen zum Gerät "
      + "(`07` §7.11).",
  },
};

function AblageListe({ art }: { art: Ablageart }) {
  const { bestand } = useSitzung();
  const [frage, setzeFrage] = useState("");
  const beschriftung = ABLAGE[art];

  const objekte = useMemo(() => {
    const q = frage.trim().toLowerCase();
    return bestand.ablage
      .filter((o) => o.art === art)
      .filter(
        (o) =>
          !q
          || o.name.toLowerCase().includes(q)
          || (o.schlagworte?.some((s) => s.toLowerCase().includes(q)) ?? false)
          || (o.inhalt?.toLowerCase().includes(q) ?? false),
      )
      .slice()
      .sort((x, y) => y.geaendert.localeCompare(x.geaendert));
  }, [bestand, art, frage]);

  return (
    <>
      <div className="bereichskopf">
        <h1>{beschriftung.titel}</h1>
        <span className="bereichskopf__zusatz">{objekte.length}</span>
      </div>

      <input
        className="suchfeld"
        type="search"
        placeholder={`${beschriftung.titel} durchsuchen`}
        aria-label={`${beschriftung.titel} durchsuchen`}
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
      />

      {objekte.length === 0 ? (
        <Leer text={`Nichts gefunden zu „${frage}".`} />
      ) : (
        <Liste>
          {objekte.map((o) => (
            <Zeile
              key={o.ref.opaqueId}
              // Derselbe Weg wie aus der Auftragsakte. `07` §7.12 nennt den
              // Auftrag den primären Zugriff — der Bereich hier ist der
              // zweite Weg, für den Fall, dass man den Auftrag nicht weiß.
              zu={hatQuellsystem(o.ref) ? `/oeffnen/${o.ref.opaqueId}` : undefined}
              titel={o.name}
              unterzeile={
                <>
                  {o.pfad ? `${o.pfad} · ` : ""}
                  {datum(o.geaendert)}
                  {o.geaendertVon ? ` · ${o.geaendertVon}` : ""}
                  {o.schlagworte?.length ? ` · ${o.schlagworte.join(", ")}` : ""}
                </>
              }
              abzeichen={
                // `07` §7.9 verlangt die Offlinekennzeichnung. Sie steht auch
                // im Portal, weil das Büro entscheidet, was der Monteur
                // mitbekommt — und dafür muss es sehen, was vorgeladen wird.
                o.offline === "vorgeladen" ? (
                  <span className="abzeichen abzeichen--erfolg">offline verfügbar</span>
                ) : o.offline === "auf-wunsch" ? (
                  <span className="abzeichen abzeichen--neutral">auf Wunsch</span>
                ) : (
                  <span className="abzeichen abzeichen--neutral">nur online</span>
                )
              }
              rechts={groesse(o.groesseBytes)}
            />
          ))}
        </Liste>
      )}

      <p className="notiz">
        Führendes System ist <strong>{beschriftung.quelle}</strong>. {beschriftung.erklaerung}
      </p>
    </>
  );
}

export function Dateien() {
  return <AblageListe art="arbeitsdatei" />;
}

export function Dokumente() {
  return <AblageListe art="dokument" />;
}

export function Wissen() {
  const { bestand } = useSitzung();
  const [offen, setzeOffen] = useState<string | undefined>(undefined);
  const seiten = bestand.ablage.filter((o) => o.art === "wissen");
  const seite = seiten.find((s) => s.ref.opaqueId === offen);

  return (
    <>
      <div className="bereichskopf">
        <h1>Wissen</h1>
        <span className="bereichskopf__zusatz">{seiten.length} Seiten</span>
      </div>

      <Liste>
        {seiten.map((s) => (
          <Zeile
            key={s.ref.opaqueId}
            titel={
              <button
                type="button"
                className="zeile__oeffnen"
                onClick={() => setzeOffen(offen === s.ref.opaqueId ? undefined : s.ref.opaqueId)}
                aria-expanded={offen === s.ref.opaqueId}
              >
                {s.name}
              </button>
            }
            unterzeile={s.schlagworte?.join(", ")}
            rechts={datum(s.geaendert)}
          />
        ))}
      </Liste>

      {seite?.inhalt && (
        <section className="karte">
          {/*
            Bewusst als vorformatierter Text und nicht als gerendertes Markdown.
            Ein Renderer im Portal wäre ein zweiter Editor neben Collectives,
            und was er darstellt, käme aus einer fremden Quelle — genau der Weg,
            auf dem `05` §5.12 Prompt-Injection und Markup-Einschleusung
            erwartet. Gelesen und bearbeitet wird in Collectives.
          */}
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "var(--hwc-schrift)",
              margin: 0,
            }}
          >
            {seite.inhalt}
          </pre>
        </section>
      )}

      <p className="notiz">
        Führendes System ist <strong>Nextcloud Collectives</strong>. Bearbeitet wird
        dort; das Portal zeigt und verknüpft.
      </p>
    </>
  );
}

// ───────────────────────── Kommunikation ─────────────────────────

/**
 * Die Kommunikationssicht des Portals.
 *
 * `07` §7.10 zieht hier die schärfste Grenze des ganzen Produkts: Das Büro
 * arbeitet in **Bulwark**, und was den Auftragsbezug hineinträgt, sind
 * Plugins — „nicht das Portal, weil sonst genau das Umschalten zwischen zwei
 * Oberflächen entstünde, das die Plattform vermeiden soll".
 *
 * Was hier bleibt, ist der Überblick: Was ist da, was ist keinem Auftrag
 * zugeordnet, wo läuft ein Chat. Kein Lesebereich für den Volltext, kein
 * Antwortfeld. Beides einzubauen wäre der erste Schritt zum zweiten
 * Mailclient.
 */
export function Kommunikation() {
  const { bestand } = useSitzung();

  const zugeordnet = new Set(
    bestand.kanten
      .filter((k) => k.relation === "mentioned_in" || k.relation === "received_via")
      .flatMap((k) => [formatResourceRef(k.source), formatResourceRef(k.target)]),
  );

  const nachrichten = bestand.nachrichten
    .slice()
    .sort((x, y) => y.gesendet.localeCompare(x.gesendet));

  const ohneBezug = nachrichten.filter((n) => !zugeordnet.has(formatResourceRef(n.ref)));

  return (
    <>
      <div className="bereichskopf">
        <h1>Kommunikation</h1>
        <span className="bereichskopf__zusatz">
          {nachrichten.filter((n) => !n.gelesen).length} ungelesen
        </span>
      </div>

      {ohneBezug.length > 0 && (
        <Hinweis titel="Nachrichten ohne Auftragsbezug." art="neutral">
          {mehrzahl(ohneBezug.length, "Nachricht ist", "Nachrichten sind")} keinem
          Auftrag zugeordnet. Das ist genau
          die Lücke, die die Plattform schließen soll — im Eingangskorb lassen sie sich
          zuordnen.
        </Hinweis>
      )}

      <h2>E-Mail</h2>
      <Liste>
        {nachrichten.map((n) => (
          <Zeile
            key={n.ref.opaqueId}
            zu={`/oeffnen/${n.ref.opaqueId}`}
            titel={n.betreff}
            unterzeile={
              <>
                {n.richtung === "eingehend" ? `von ${n.von}` : `an ${n.an.join(", ")}`} ·{" "}
                {n.ausschnitt}
              </>
            }
            abzeichen={
              <>
                {!n.gelesen && (
                  <span className="abzeichen abzeichen--warnung">ungelesen</span>
                )}
                {!zugeordnet.has(formatResourceRef(n.ref)) && (
                  <span className="abzeichen abzeichen--neutral">ohne Auftrag</span>
                )}
              </>
            }
            rechts={`${datum(n.gesendet)}\n${uhrzeit(n.gesendet)}`}
          />
        ))}
      </Liste>

      <h2>Bürochat</h2>
      <Liste>
        {bestand.chatraeume.map((r) => {
          const auftrag = findeAuftrag(bestand, r.auftrag);
          const beitraege = bestand.chatbeitraege.filter(
            (b) => formatResourceRef(b.raum) === formatResourceRef(r.ref),
          );
          const letzter = beitraege[beitraege.length - 1];
          return (
            <Zeile
              key={r.ref.opaqueId}
              zu={auftrag ? zumAuftrag(auftrag.nummer) : undefined}
              titel={r.name}
              unterzeile={
                letzter
                  ? `${findePerson(bestand, letzter.autor)?.name}: ${letzter.text}`
                  : "noch keine Nachricht"
              }
              rechts={
                <>
                  {beitraege.length} Beiträge
                  {letzter && (
                    <>
                      <br />
                      {datum(letzter.gesendet)}
                    </>
                  )}
                </>
              }
            />
          );
        })}
      </Liste>

      <p className="notiz">
        Verfasst und beantwortet wird in <strong>Bulwark</strong> (ADR-019), der
        Auftragsbezug kommt dort aus Plugins. Anrufe und Videokonferenz sind nach
        ADR-018 nicht Bestandteil von Version 1.
      </p>
    </>
  );
}

// ───────────────────────── Administration ─────────────────────────

/**
 * Administration — was `07` §7.2 unter Inhaber/Administrator führt: Benutzer
 * und Rechte, Betriebs- und Sicherheitsstatus, Freigaben und Audit.
 *
 * Der Bereich ist in der Navigation an die Rolle `tenant-admin` gebunden. Das
 * ist nach `navigation.ts` ausdrücklich **keine** Berechtigungsprüfung — die
 * entscheidet nach `01` §1.11 das Quellsystem —, sondern eine Frage der
 * Brauchbarkeit. Deshalb steht hier trotzdem kein Wächter davor: Ein zweiter,
 * scheinbarer Schutz im Portal wäre genau die Sorte Sicherheit, die man später
 * für echt hält.
 */
export function Administration() {
  const { bestand, heute, person, fuehreAus, vorgaenge, verwirfVorgaenge } =
    useSitzung();
  const links = bestand.magiclinks;
  const bald = baldAblaufendeLinks(bestand, heute);

  return (
    <>
      <h1>Administration</h1>

      <h2>Benutzer und Rollen</h2>
      <Liste>
        {bestand.personen.map((p) => (
          <Zeile
            key={p.ref.opaqueId}
            titel={p.name}
            unterzeile={
              <>
                {p.email} · {p.rollen.join(", ")}
              </>
            }
            abzeichen={
              p.verfuegbar === false ? (
                <span className="abzeichen abzeichen--warnung">
                  {p.abwesenheitsgrund ?? "abwesend"}
                </span>
              ) : p.rollen.includes("external-partner") ? (
                <span className="abzeichen abzeichen--neutral">externer Partner</span>
              ) : null
            }
            rechts={p.kuerzel}
          />
        ))}
      </Liste>
      {/*
        Der Unterschied, den `07` §7.2 als „wesentlich" bezeichnet — und der
        Grund, aus dem ihn `05` §5.7 nicht über MagicLinks abbilden lässt.
      */}
      <p className="notiz">
        Der <strong>externe Partner</strong> — typischerweise der Steuerberater — hat ein
        eigenes Konto mit engem, unbefristetem, aber widerrufbarem Leserecht. Der{" "}
        <strong>Gast</strong> bekommt stattdessen einen MagicLink mit Ablaufdatum. Beides
        über MagicLinks abzubilden würde die Sieben-Tage-Regel aus `05` §5.7 aushöhlen.
      </p>

      <h2>MagicLinks</h2>
      {bald.length > 0 && (
        <Hinweis titel="Es laufen Zugänge ab.">
          {bald.length} MagicLink{bald.length === 1 ? "" : "s"} {bald.length === 1 ? "läuft" : "laufen"}{" "}
          in den nächsten drei Tagen ab.
        </Hinweis>
      )}
      <Liste>
        {links.map((m) => (
          <Zeile
            key={m.ref.opaqueId}
            titel={m.zweck}
            unterzeile={
              <>
                {m.empfaenger} · erstellt {datum(m.erstellt)} · {m.zugriffe}{" "}
                {m.zugriffe === 1 ? "Zugriff" : "Zugriffe"}
              </>
            }
            abzeichen={
              m.widerrufen ? (
                <span className="abzeichen abzeichen--aus">widerrufen</span>
              ) : bald.some((b) => b.ref.opaqueId === m.ref.opaqueId) ? (
                <span className="abzeichen abzeichen--warnung">läuft bald ab</span>
              ) : (
                <span className="abzeichen abzeichen--erfolg">gültig</span>
              )
            }
            rechts={
              m.widerrufen ? (
                `bis ${datum(m.laeuftAb)}`
              ) : (
                <Knopf
                  type="button"
                  art="still"
                  onClick={() =>
                    fuehreAus((v) =>
                      widerrufeLink({
                        vorgaenge: v,
                        linkRef: m.ref,
                        akteur: person.ref,
                      }),
                    )
                  }
                >
                  Widerrufen
                </Knopf>
              )
            }
          />
        ))}
      </Liste>
      {/*
        Ein Widerruf wirkt sofort und ist nicht rückgängig zu machen — deshalb
        steht das hier und nicht erst im Bestätigungsdialog, den es bewusst
        nicht gibt. `04` §4.9 kennt den Widerruf als eigenen Vorgang; ihn
        umständlich zu machen hieße, im Zweifel zu zögern, und im Zweifel ist
        Widerrufen die richtige Entscheidung.
      */}
      <p className="notiz">
        Ein Widerruf wirkt sofort. Der Empfänger bekommt danach eine Absage statt des
        Inhalts; ein neuer Zugang wäre ein neuer Link.
      </p>

      <h2>Audit</h2>
      <p className="leise">
        Jeder Statuswechsel ist nach `05` §5.15 auditpflichtig. Bei stellvertretendem
        Schalten protokolliert das Audit beide — den Schaltenden und den Zugewiesenen
        (ADR-045).
      </p>
      <Liste>
        {bestand.audit
          .slice()
          .sort((x, y) => y.zeitpunkt.localeCompare(x.zeitpunkt))
          .map((a) => (
            <Zeile
              key={a.ref.opaqueId}
              titel={a.handlung}
              unterzeile={
                <>
                  {findePerson(bestand, a.akteur)?.name ?? a.akteur.opaqueId}
                  {a.imAuftragVon && (
                    <> im Auftrag von {findePerson(bestand, a.imAuftragVon)?.name}</>
                  )}
                  {a.von && a.nach && ` · ${a.von} → ${a.nach}`}
                </>
              }
              rechts={`${datum(a.zeitpunkt)}\n${uhrzeit(a.zeitpunkt)}`}
            />
          ))}
      </Liste>

      <h2>Betrieb</h2>
      <Felder>
        <Feld name="Mandant">{bestand.tenant}</Feld>
        <Feld name="Betrieb">{bestand.betrieb.name}</Feld>
        <Feld name="Benutzer">{bestand.personen.length}</Feld>
        <Feld name="Kunden">{bestand.kunden.length}</Feld>
        <Feld name="Aufträge">{bestand.auftraege.length}</Feld>
        <Feld name="Verknüpfungen im Graph">{bestand.kanten.length}</Feld>
      </Felder>
      <p className="notiz">
        Betriebs- und Sicherheitsstatus kommen im Zielbild aus Uptime Kuma und der
        Control Plane (ADR-022, ADR-026). Alarme laufen über genau eine Kette — weder
        die Control Plane noch der Fehlerberichtsdienst alarmieren.
      </p>

      <h2>Diesen Probierstand zurücksetzen</h2>
      {/*
        Was diese Sitzung entschieden hat, liegt im Browser und nicht in einem
        Dienst — der Kommentar in `daten/Sitzung.tsx` sagt, warum. Damit
        braucht es einen Weg zurück: Ohne ihn häufen sich Zuordnungen,
        Freigaben und Statuswechsel an, bis niemand mehr weiß, was vom
        Demobestand stammt und was vom Ausprobieren.
      */}
      <p className="leise">
        {vorgaenge.audit.length === 0
          ? "In dieser Sitzung wurde noch nichts entschieden."
          : `${vorgaenge.audit.length} Entscheidung${
              vorgaenge.audit.length === 1 ? "" : "en"
            } in dieser Sitzung: Zuordnungen, Statuswechsel, Freigaben, `
            + `${vorgaenge.neueAuftraege.length} neue Aufträge. `
            + "Zurücksetzen löscht sie und stellt den Demobestand wieder her — "
            + "er selbst ist unveränderlich."}
      </p>
      {vorgaenge.audit.length > 0 && (
        <div className="filter">
          <Knopf type="button" art="still" onClick={verwirfVorgaenge}>
            Alles zurücksetzen
          </Knopf>
        </div>
      )}

      <h2>Werkzeuge</h2>
      <p className="leise">
        Kein Bereich des Produkts, sondern die Gegenstände für das Prüfrezept aus{" "}
        <code>tests/ui/README.md</code>. Sie stehen nicht in der Navigation und sind
        hier verlinkt, damit sie auffindbar bleiben statt nur in einer Testdatei zu
        existieren.
      </p>
      <ul>
        <li>
          <Link to="/proben">Rahmenproben und Fehlergrenze</Link>
        </li>
        <li>
          <Link to="/bauteile">Bauteile des Designsystems</Link>
        </li>
        <li>
          <Link to="/suche">Einheitliche Suche</Link> — hat nach <code>07</code> §7.3
          keinen Navigationspunkt, aber ein Paket (<code>PORTAL-003</code>)
        </li>
        <li>
          <Link to="/wartung">Wartungsplanung</Link> — aus demselben Grund
          (<code>MAINT-001</code>)
        </li>
        <li>
          <Link to="/erechnungen">E-Rechnungen</Link> — ebenso
          (<code>EINV-001</code>)
        </li>
        <li>
          <Link to="/uebergabe">Übergabe an ERP und Steuerberater</Link> —
          (<code>EXP-001</code>)
        </li>
      </ul>
    </>
  );
}

// ───────────────────────── Assistent ─────────────────────────

/**
 * Der KI-Assistent.
 *
 * Der Navigationspunkt erscheint nur, wenn `features.ai` im Stack an ist —
 * und der Pilot startet nach ADR-017 mit `false`. Diese Ansicht ist deshalb
 * normalerweise nicht erreichbar. Sie existiert trotzdem, damit hinter einem
 * eingeschalteten Merkmal keine leere Seite steht.
 */
export function Assistent() {
  return (
    <>
      <h1>Assistent</h1>
      <Hinweis titel="Der Assistent ist in Version 1 nicht enthalten.">
        ADR-017 hält die KI vollständig hinter dem Piloten. Bis dahin — und in Version 1
        auch danach — deckt die <Link to="/suche">Suche ohne KI</Link> die
        Auffindbarkeit über alle Systeme ab.
      </Hinweis>
      <p>Was der Assistent ab M8 können soll, steht in `07` §7.13. Die Regeln dazu sind
        heute schon festgelegt: Jede Antwort zeigt ihre Quellen, und jede Aktion —
        Statusänderung, E-Mail-Versand, MagicLink — braucht eine sichtbare Bestätigung,
        auch wenn der Vorschlag aus der KI stammt (`05` §5.12).</p>
    </>
  );
}
